import { useState, useEffect } from 'react';
import { taskController } from '../../controllers/taskcontroller';
import { projectController } from '../../controllers/projectController';
import { employeeController } from '../../controllers/employeeController';
import { 
  Plus, Search, Download, Eye, Edit2, Trash2, X, AlertCircle, 
  ChevronUp, ChevronDown, CheckSquare, Clock, AlertTriangle,
  TrendingUp, Filter, Calendar, Users, BarChart3
} from 'lucide-react';

const TasksPage = () => {
  const [tasks, setTasks] = useState([]);
  const [projects, setProjects] = useState([]);
  const [users, setUsers] = useState([]);
  const [filteredTasks, setFilteredTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('All');
  const [filterPriority, setFilterPriority] = useState('All');
  const [filterProject, setFilterProject] = useState('All');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    projectId: '',
    assignedTo: '',
    status: 'To Do',
    priority: 'Medium',
    startDate: '',
    dueDate: '',
    estimatedHours: 0,
    actualHours: 0,
    progress: 0
  });
  const [formErrors, setFormErrors] = useState([]);
  const [formSubmitting, setFormSubmitting] = useState(false);
  const [sortField, setSortField] = useState('createdAt');
  const [sortDirection, setSortDirection] = useState('desc');
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedTasks, setSelectedTasks] = useState([]);
  const itemsPerPage = 10;

  useEffect(() => { 
    fetchTasks(); 
    fetchProjects();
    fetchUsers();
  }, []);

  useEffect(() => {
    let result = [...tasks];
    
    if (searchQuery) {
      result = result.filter(t =>
        (t.title || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
        (t.taskId || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
        (t.description || '').toLowerCase().includes(searchQuery.toLowerCase())
      );
    }
    
    if (filterStatus !== 'All') {
      result = result.filter(t => t.status === filterStatus);
    }
    
    if (filterPriority !== 'All') {
      result = result.filter(t => t.priority === filterPriority);
    }
    
    if (filterProject !== 'All') {
      result = result.filter(t => {
        const projectId = t.projectId?._id || t.projectId;
        return projectId === filterProject;
      });
    }
    
    result.sort((a, b) => {
      let aVal = a[sortField], bVal = b[sortField];
      if (typeof aVal === 'string') {
        return sortDirection === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
      }
      return sortDirection === 'asc' ? aVal - bVal : bVal - aVal;
    });
    
    setFilteredTasks(result);
    setCurrentPage(1);
  }, [tasks, searchQuery, filterStatus, filterPriority, filterProject, sortField, sortDirection]);

  const fetchTasks = async () => {
    setLoading(true);
    const result = await taskController.getAll();
    if (result.success) {
      setTasks(result.data);
    } else {
      alert('Error: ' + result.message);
    }
    setLoading(false);
  };

  const fetchProjects = async () => {
    const result = await projectController.getAll();
    if (result.success) {
      setProjects(result.data);
    }
  };

  const fetchUsers = async () => {
    const result = await employeeController.getAll();
    if (result.success) {
      setUsers(result.data);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    setFormErrors([]);
  };

  const handleAdd = () => {
    setFormData({
      title: '',
      description: '',
      projectId: '',
      assignedTo: '',
      status: 'To Do',
      priority: 'Medium',
      startDate: '',
      dueDate: '',
      estimatedHours: 0,
      actualHours: 0,
      progress: 0
    });
    setFormErrors([]);
    setShowAddModal(true);
  };

  const handleAddSubmit = async (e) => {
    e.preventDefault();
    setFormSubmitting(true);
    setFormErrors([]);
    
    const dataToSend = { ...formData };
    // Don't send assignedTo if not selected
    if (!dataToSend.assignedTo || dataToSend.assignedTo === '') {
      delete dataToSend.assignedTo;
    }
    
    const result = await taskController.create(dataToSend);
    if (result.success) {
      setShowAddModal(false);
      fetchTasks();
      alert('✅ Task created successfully!');
    } else {
      setFormErrors(result.errors || [result.message]);
    }
    setFormSubmitting(false);
  };

  const handleEdit = (task) => {
    setSelectedTask(task);
    
    // Get assignedTo ID properly
    let assignedToValue = '';
    if (task.assignedTo) {
      if (typeof task.assignedTo === 'object' && (task.assignedTo.id || task.assignedTo._id)) {
        assignedToValue = task.assignedTo.id || task.assignedTo._id;
      } else if (typeof task.assignedTo === 'string' && task.assignedTo.length === 24) {
        assignedToValue = task.assignedTo;
      }
    }
    
    // Get projectId properly
    let projectIdValue = '';
    if (task.projectId) {
      if (typeof task.projectId === 'object' && (task.projectId.id || task.projectId._id)) {
        projectIdValue = task.projectId.id || task.projectId._id;
      } else if (typeof task.projectId === 'string') {
        projectIdValue = task.projectId;
      }
    }
    
    setFormData({
      title: task.title || '',
      description: task.description || '',
      projectId: projectIdValue,
      assignedTo: assignedToValue,
      status: task.status || 'To Do',
      priority: task.priority || 'Medium',
      startDate: task.startDate?.split('T')[0] || '',
      dueDate: task.dueDate?.split('T')[0] || '',
      estimatedHours: task.estimatedHours || 0,
      actualHours: task.actualHours || 0,
      progress: task.completionPercentage || 0
    });
    setFormErrors([]);
    setShowEditModal(true);
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    setFormSubmitting(true);
    setFormErrors([]);
    
    const dataToSend = { ...formData };
    // Don't send assignedTo if not selected
    if (!dataToSend.assignedTo || dataToSend.assignedTo === '') {
      delete dataToSend.assignedTo;
    }
    
    const result = await taskController.update(selectedTask.id || selectedTask._id, dataToSend);
    if (result.success) {
      setShowEditModal(false);
      fetchTasks();
      alert('✅ Task updated successfully!');
    } else {
      setFormErrors(result.errors || [result.message]);
    }
    setFormSubmitting(false);
  };

  const handleView = (task) => {
    setSelectedTask(task);
    setShowViewModal(true);
  };

  const handleDelete = async (task) => {
    try {
      const isConfirmed = window.confirm(
        `Delete task "${task.title}"?`
      );
  
      if (!isConfirmed) return;
  
      const result = await taskController.delete(task.id || task._id);
  
      if (result?.success) {
        await fetchTasks();
        alert('✅ Task deleted successfully!');
      } else {
        alert(result?.message || '❌ Failed to delete task');
      }
    } catch (error) {
      console.error('Delete task error:', error);
      alert('❌ Something went wrong while deleting task');
    }
  };

  const handleStatusChange = async (task, newStatus) => {
    const result = await taskController.updateStatus(task.id || task._id, newStatus);
    if (result.success) {
      fetchTasks();
      alert('✅ Status updated!');
    } else {
      alert('Error: ' + result.message);
    }
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const handleExport = async () => {
    const filters = {};
    if (filterStatus !== 'All') filters.status = filterStatus;
    if (filterPriority !== 'All') filters.priority = filterPriority;
    if (filterProject !== 'All') filters.projectId = filterProject;
    
    const result = await taskController.export(filters, 'csv');
    if (result.success) {
      const blob = new Blob([result.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `tasks-${new Date().toISOString().split('T')[0]}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
      alert('✅ Tasks exported successfully!');
    } else {
      alert('Error: ' + result.message);
    }
  };

  const handleSelectTask = (taskId) => {
    setSelectedTasks(prev => 
      prev.includes(taskId) 
        ? prev.filter(id => id !== taskId)
        : [...prev, taskId]
    );
  };

  const handleSelectAll = () => {
    if (selectedTasks.length === currentTasks.length) {
      setSelectedTasks([]);
    } else {
      setSelectedTasks(currentTasks.map(t => t.id || t._id));
    }
  };

  const handleBulkDelete = async () => {
    if (selectedTasks.length === 0) {
      alert('Please select tasks to delete');
      return;
    }
    
    const confirmed = window.confirm(`Delete ${selectedTasks.length} selected tasks?`);
    if (!confirmed) return;
    
    const result = await taskController.bulkDelete(selectedTasks);
    if (result.success) {
      setSelectedTasks([]);
      fetchTasks();
      alert('✅ Tasks deleted successfully!');
    } else {
      alert('Error: ' + result.message);
    }
  };

  const handleBulkStatusUpdate = async (newStatus) => {
    if (selectedTasks.length === 0) {
      alert('Please select tasks to update');
      return;
    }
    
    const result = await taskController.bulkUpdate(selectedTasks, { status: newStatus });
    if (result.success) {
      setSelectedTasks([]);
      fetchTasks();
      alert('✅ Tasks updated successfully!');
    } else {
      alert('Error: ' + result.message);
    }
  };

  // Helper functions
  const isOverdue = (task) => {
    if (!task.dueDate) return false;
    return new Date(task.dueDate) < new Date() && task.status !== 'Completed';
  };

  const getDaysUntilDue = (task) => {
    if (!task.dueDate) return null;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const due = new Date(task.dueDate);
    due.setHours(0, 0, 0, 0);
    return Math.ceil((due - today) / (1000 * 60 * 60 * 24));
  };

  const getAssigneeName = (task) => {
    if (!task.assignedTo) return 'Unassigned';
    if (typeof task.assignedTo === 'object') {
      return task.assignedTo.fullName || task.assignedTo.name || task.assignedTo.email || 'Unknown User';
    }
    return 'Assigned';
  };

  const getStatusColor = (status) => {
    const colors = {
      'To Do': '#64748b',
      'In Progress': '#f59e0b',
      'Review': '#8b5cf6',
      'Completed': '#10b981',
      'Blocked': '#ef4444'
    };
    return colors[status] || '#64748b';
  };

  const getPriorityColor = (priority) => {
    const colors = {
      'Low': '#10b981',
      'Medium': '#f59e0b',
      'High': '#ef4444'
    };
    return colors[priority] || '#64748b';
  };

  const getProjectName = (projectId) => {
    if (typeof projectId === 'object' && projectId !== null) {
      return projectId.name || projectId.projectName || 'Unknown Project';
    }
    const project = projects.find(p => (p.id || p._id) === projectId);
    return project ? (project.name || project.projectName) : 'Unknown Project';
  };

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentTasks = filteredTasks.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredTasks.length / itemsPerPage);

  const stats = {
    total: tasks.length,
    todo: tasks.filter(t => t.status === 'To Do').length,
    inProgress: tasks.filter(t => t.status === 'In Progress').length,
    completed: tasks.filter(t => t.status === 'Completed').length,
    overdue: tasks.filter(t => isOverdue(t)).length
  };

  const styles = {
    container: { padding: 0 },
    header: { 
      display: 'flex', 
      justifyContent: 'space-between', 
      alignItems: 'center', 
      marginBottom: '24px', 
      flexWrap: 'wrap', 
      gap: '16px' 
    },
    title: { fontSize: '28px', fontWeight: 'bold', color: '#1e293b', margin: 0 },
    button: { 
      display: 'flex', 
      alignItems: 'center', 
      gap: '8px', 
      padding: '10px 20px', 
      backgroundColor: '#3b82f6', 
      color: '#fff', 
      border: 'none', 
      borderRadius: '8px', 
      fontSize: '14px', 
      fontWeight: '500', 
      cursor: 'pointer' 
    },
    secondaryButton: { 
      backgroundColor: '#fff', 
      color: '#1e293b', 
      border: '1px solid #e2e8f0' 
    },
    dangerButton: {
      backgroundColor: '#ef4444',
      color: '#fff'
    },
    statsBar: { 
      display: 'grid', 
      gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', 
      gap: '16px', 
      marginBottom: '24px' 
    },
    statCard: { 
      backgroundColor: '#fff', 
      padding: '20px', 
      borderRadius: '12px', 
      boxShadow: '0 1px 3px rgba(0,0,0,0.1)' 
    },
    statLabel: { fontSize: '14px', color: '#64748b', marginBottom: '8px' },
    statValue: { fontSize: '28px', fontWeight: 'bold', color: '#1e293b' },
    filterBar: { 
      display: 'flex', 
      gap: '12px', 
      marginBottom: '20px', 
      flexWrap: 'wrap',
      alignItems: 'center'
    },
    searchBox: { flex: 1, minWidth: '250px', position: 'relative' },
    searchInput: { 
      width: '100%', 
      padding: '10px 10px 10px 40px', 
      border: '1px solid #e2e8f0', 
      borderRadius: '8px', 
      fontSize: '14px', 
      outline: 'none' 
    },
    searchIcon: { 
      position: 'absolute', 
      left: '12px', 
      top: '50%', 
      transform: 'translateY(-50%)' 
    },
    select: { 
      padding: '10px 16px', 
      border: '1px solid #e2e8f0', 
      borderRadius: '8px', 
      fontSize: '14px', 
      outline: 'none', 
      backgroundColor: '#fff', 
      cursor: 'pointer' 
    },
    card: { 
      backgroundColor: '#fff', 
      borderRadius: '12px', 
      boxShadow: '0 1px 3px rgba(0,0,0,0.1)', 
      overflow: 'hidden' 
    },
    table: { width: '100%', borderCollapse: 'collapse' },
    th: { 
      backgroundColor: '#f8fafc', 
      padding: '16px', 
      textAlign: 'left', 
      fontSize: '13px', 
      fontWeight: '600', 
      color: '#475569', 
      borderBottom: '1px solid #e2e8f0', 
      cursor: 'pointer', 
      userSelect: 'none' 
    },
    td: { 
      padding: '16px', 
      borderBottom: '1px solid #e2e8f0', 
      fontSize: '14px', 
      color: '#1e293b' 
    },
    badge: { 
      display: 'inline-block', 
      padding: '4px 12px', 
      borderRadius: '12px', 
      fontSize: '12px', 
      fontWeight: '500' 
    },
    actionButton: { 
      padding: '6px', 
      backgroundColor: 'transparent', 
      border: 'none', 
      borderRadius: '6px', 
      cursor: 'pointer' 
    },
    pagination: { 
      display: 'flex', 
      justifyContent: 'space-between', 
      alignItems: 'center', 
      padding: '20px', 
      borderTop: '1px solid #e2e8f0' 
    },
    modal: { 
      position: 'fixed', 
      top: 0, 
      left: 0, 
      right: 0, 
      bottom: 0, 
      backgroundColor: 'rgba(0,0,0,0.5)', 
      display: 'flex', 
      alignItems: 'center', 
      justifyContent: 'center', 
      zIndex: 1000, 
      padding: '20px' 
    },
    modalContent: { 
      backgroundColor: '#fff', 
      borderRadius: '12px', 
      width: '100%', 
      maxWidth: '800px', 
      maxHeight: '90vh', 
      overflow: 'auto' 
    },
    modalHeader: { 
      padding: '20px 24px', 
      borderBottom: '1px solid #e2e8f0', 
      display: 'flex', 
      justifyContent: 'space-between', 
      alignItems: 'center' 
    },
    modalTitle: { fontSize: '20px', fontWeight: '600', color: '#1e293b', margin: 0 },
    modalBody: { padding: '24px' },
    formGroup: { marginBottom: '20px' },
    label: { 
      display: 'block', 
      marginBottom: '8px', 
      fontSize: '14px', 
      fontWeight: '500', 
      color: '#374151' 
    },
    input: { 
      width: '100%', 
      padding: '10px 12px', 
      border: '1px solid #e2e8f0', 
      borderRadius: '8px', 
      fontSize: '14px', 
      outline: 'none' 
    },
    textarea: { 
      width: '100%', 
      padding: '10px 12px', 
      border: '1px solid #e2e8f0', 
      borderRadius: '8px', 
      fontSize: '14px', 
      outline: 'none', 
      minHeight: '100px', 
      resize: 'vertical' 
    },
    errorList: { 
      backgroundColor: '#fee2e2', 
      border: '1px solid #fecaca', 
      borderRadius: '8px', 
      padding: '12px 16px', 
      marginBottom: '20px' 
    },
    errorItem: { 
      color: '#dc2626', 
      fontSize: '14px', 
      marginBottom: '4px', 
      display: 'flex', 
      alignItems: 'center', 
      gap: '8px' 
    },
    modalFooter: { 
      padding: '16px 24px', 
      borderTop: '1px solid #e2e8f0', 
      display: 'flex', 
      justifyContent: 'flex-end', 
      gap: '12px' 
    },
    progressBar: { 
      width: '100%', 
      height: '8px', 
      backgroundColor: '#e2e8f0', 
      borderRadius: '4px', 
      overflow: 'hidden' 
    },
    progressFill: { 
      height: '100%', 
      backgroundColor: '#3b82f6', 
      transition: 'width 0.3s' 
    },
    checkbox: {
      width: '18px',
      height: '18px',
      cursor: 'pointer'
    }
  };

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h1 style={styles.title}>All Tasks</h1>
        <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
          {selectedTasks.length > 0 && (
            <>
              <button 
                style={{ ...styles.button, ...styles.dangerButton }} 
                onClick={handleBulkDelete}
              >
                <Trash2 size={18} /> Delete ({selectedTasks.length})
              </button>
              <select 
                style={styles.select}
                onChange={(e) => {
                  if (e.target.value) {
                    handleBulkStatusUpdate(e.target.value);
                    e.target.value = '';
                  }
                }}
              >
                <option value="">Update Status</option>
                <option value="To Do">To Do</option>
                <option value="In Progress">In Progress</option>
                <option value="Review">Review</option>
                <option value="Completed">Completed</option>
              </select>
            </>
          )}
          <button 
            style={{ ...styles.button, ...styles.secondaryButton }} 
            onClick={handleExport}
          >
            <Download size={18} /> Export
          </button>
          <button style={styles.button} onClick={handleAdd}>
            <Plus size={18} /> Add Task
          </button>
        </div>
      </div>

      <div style={styles.statsBar}>
        <div style={styles.statCard}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
            <div style={styles.statLabel}>Total Tasks</div>
            <CheckSquare size={24} color="#3b82f6" />
          </div>
          <div style={styles.statValue}>{stats.total}</div>
        </div>
        <div style={styles.statCard}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
            <div style={styles.statLabel}>In Progress</div>
            <TrendingUp size={24} color="#f59e0b" />
          </div>
          <div style={{ ...styles.statValue, color: '#f59e0b' }}>{stats.inProgress}</div>
        </div>
        <div style={styles.statCard}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
            <div style={styles.statLabel}>Completed</div>
            <CheckSquare size={24} color="#10b981" />
          </div>
          <div style={{ ...styles.statValue, color: '#10b981' }}>{stats.completed}</div>
        </div>
        <div style={styles.statCard}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
            <div style={styles.statLabel}>Overdue</div>
            <AlertTriangle size={24} color="#ef4444" />
          </div>
          <div style={{ ...styles.statValue, color: '#ef4444' }}>{stats.overdue}</div>
        </div>
      </div>

      <div style={styles.filterBar}>
        <div style={styles.searchBox}>
          <Search size={18} color="#94a3b8" style={styles.searchIcon} />
          <input 
            type="text" 
            placeholder="Search tasks..." 
            style={styles.searchInput} 
            value={searchQuery} 
            onChange={(e) => setSearchQuery(e.target.value)} 
          />
        </div>
        <select 
          style={styles.select} 
          value={filterProject} 
          onChange={(e) => setFilterProject(e.target.value)}
        >
          <option value="All">All Projects</option>
          {projects.map(project => (
            <option key={project.id || project._id} value={project.id || project._id}>
              {project.name || project.projectName}
            </option>
          ))}
        </select>
        <select 
          style={styles.select} 
          value={filterStatus} 
          onChange={(e) => setFilterStatus(e.target.value)}
        >
          <option value="All">All Status</option>
          <option value="To Do">To Do</option>
          <option value="In Progress">In Progress</option>
          <option value="Review">Review</option>
          <option value="Completed">Completed</option>
          <option value="Blocked">Blocked</option>
        </select>
        <select 
          style={styles.select} 
          value={filterPriority} 
          onChange={(e) => setFilterPriority(e.target.value)}
        >
          <option value="All">All Priority</option>
          <option value="Low">Low</option>
          <option value="Medium">Medium</option>
          <option value="High">High</option>
        </select>
      </div>

      <div style={styles.card}>
        {loading ? (
          <div style={{ padding: '40px', textAlign: 'center', color: '#64748b' }}>
            Loading tasks...
          </div>
        ) : currentTasks.length === 0 ? (
          <div style={{ padding: '40px', textAlign: 'center', color: '#64748b' }}>
            No tasks found. Click "Add Task" to create one.
          </div>
        ) : (
          <>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={{ ...styles.th, width: '50px' }}>
                    <input 
                      type="checkbox"
                      style={styles.checkbox}
                      checked={selectedTasks.length === currentTasks.length && currentTasks.length > 0}
                      onChange={handleSelectAll}
                    />
                  </th>
                  <th style={styles.th} onClick={() => handleSort('taskId')}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      ID {sortField === 'taskId' && (sortDirection === 'asc' ? <ChevronUp size={16} /> : <ChevronDown size={16} />)}
                    </div>
                  </th>
                  <th style={styles.th} onClick={() => handleSort('title')}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      Task Name {sortField === 'title' && (sortDirection === 'asc' ? <ChevronUp size={16} /> : <ChevronDown size={16} />)}
                    </div>
                  </th>
                  <th style={styles.th}>Project</th>
                  <th style={styles.th}>Status</th>
                  <th style={styles.th}>Priority</th>
                  <th style={styles.th}>Due Date</th>
                  <th style={styles.th}>Progress</th>
                  <th style={styles.th}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {currentTasks.map((task) => {
                  const taskIsOverdue = isOverdue(task);
                  const daysUntilDue = getDaysUntilDue(task);
                  const progress = task.completionPercentage || 0;
                  
                  return (
                    <tr key={task.id || task._id} style={{ cursor: 'pointer' }}>
                      <td style={styles.td}>
                        <input 
                          type="checkbox"
                          style={styles.checkbox}
                          checked={selectedTasks.includes(task.id || task._id)}
                          onChange={() => handleSelectTask(task.id || task._id)}
                          onClick={(e) => e.stopPropagation()}
                        />
                      </td>
                      <td style={styles.td}>
                        <div style={{ fontSize: '12px', color: '#64748b', fontWeight: '500' }}>
                          {task.taskId}
                        </div>
                      </td>
                      <td style={styles.td} onClick={() => handleView(task)}>
                        <div style={{ fontWeight: '500' }}>{task.title}</div>
                        <div style={{ fontSize: '12px', color: '#64748b' }}>
                          {getAssigneeName(task)}
                        </div>
                      </td>
                      <td style={styles.td}>
                        <div style={{ fontSize: '13px' }}>
                          {getProjectName(task.projectId)}
                        </div>
                      </td>
                      <td style={styles.td}>
                        <select
                          style={{
                            ...styles.badge,
                            backgroundColor: `${getStatusColor(task.status)}20`,
                            color: getStatusColor(task.status),
                            border: 'none',
                            cursor: 'pointer'
                          }}
                          value={task.status}
                          onChange={(e) => {
                            e.stopPropagation();
                            handleStatusChange(task, e.target.value);
                          }}
                          onClick={(e) => e.stopPropagation()}
                        >
                          <option value="To Do">To Do</option>
                          <option value="In Progress">In Progress</option>
                          <option value="Review">Review</option>
                          <option value="Completed">Completed</option>
                          <option value="Blocked">Blocked</option>
                        </select>
                      </td>
                      <td style={styles.td}>
                        <span style={{ 
                          ...styles.badge, 
                          backgroundColor: `${getPriorityColor(task.priority)}20`, 
                          color: getPriorityColor(task.priority) 
                        }}>
                          {task.priority}
                        </span>
                      </td>
                      <td style={styles.td}>
                        <div style={{ 
                          fontWeight: '500',
                          color: taskIsOverdue ? '#ef4444' : '#1e293b'
                        }}>
                          {task.dueDate ? new Date(task.dueDate).toLocaleDateString() : 'No due date'}
                        </div>
                        {task.dueDate && !taskIsOverdue && daysUntilDue >= 0 && (
                          <div style={{ fontSize: '12px', color: '#64748b' }}>
                            {daysUntilDue === 0 ? 'Due today' : `${daysUntilDue} days left`}
                          </div>
                        )}
                        {taskIsOverdue && (
                          <div style={{ fontSize: '12px', color: '#ef4444', display: 'flex', alignItems: 'center', gap: '4px' }}>
                            <AlertTriangle size={12} /> Overdue
                          </div>
                        )}
                      </td>
                      <td style={styles.td}>
                        <div style={{ marginBottom: '4px', fontSize: '12px', color: '#64748b' }}>
                          {progress}%
                        </div>
                        <div style={styles.progressBar}>
                          <div style={{ ...styles.progressFill, width: `${progress}%` }} />
                        </div>
                      </td>
                      <td style={styles.td}>
                        <div style={{ display: 'flex', gap: '4px' }}>
                          <button 
                            style={styles.actionButton} 
                            onClick={(e) => { e.stopPropagation(); handleView(task); }} 
                            title="View"
                          >
                            <Eye size={18} color="#64748b" />
                          </button>
                          <button 
                            style={styles.actionButton} 
                            onClick={(e) => { e.stopPropagation(); handleEdit(task); }} 
                            title="Edit"
                          >
                            <Edit2 size={18} color="#3b82f6" />
                          </button>
                          <button 
                            style={styles.actionButton} 
                            onClick={(e) => { e.stopPropagation(); handleDelete(task); }} 
                            title="Delete"
                          >
                            <Trash2 size={18} color="#ef4444" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>

            <div style={styles.pagination}>
              <div style={{ fontSize: '14px', color: '#64748b' }}>
                Showing {indexOfFirstItem + 1} to {Math.min(indexOfLastItem, filteredTasks.length)} of {filteredTasks.length} tasks
              </div>
              <div style={{ display: 'flex', gap: '8px' }}>
                <button 
                  style={{ ...styles.button, ...styles.secondaryButton }} 
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))} 
                  disabled={currentPage === 1}
                >
                  Previous
                </button>
                <div style={{ display: 'flex', alignItems: 'center', padding: '0 12px', fontSize: '14px', color: '#64748b' }}>
                  Page {currentPage} of {totalPages}
                </div>
                <button 
                  style={{ ...styles.button, ...styles.secondaryButton }} 
                  onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} 
                  disabled={currentPage === totalPages}
                >
                  Next
                </button>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Add/Edit Modal */}
      {(showAddModal || showEditModal) && (
        <div style={styles.modal} onClick={() => { setShowAddModal(false); setShowEditModal(false); }}>
          <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <h2 style={styles.modalTitle}>
                {showAddModal ? 'Add New Task' : 'Edit Task'}
              </h2>
              <button 
                style={{ ...styles.actionButton, padding: '8px' }} 
                onClick={() => { setShowAddModal(false); setShowEditModal(false); }}
              >
                <X size={20} />
              </button>
            </div>

            <form onSubmit={showAddModal ? handleAddSubmit : handleEditSubmit}>
              <div style={styles.modalBody}>
                {formErrors.length > 0 && (
                  <div style={styles.errorList}>
                    {formErrors.map((error, index) => (
                      <div key={index} style={styles.errorItem}>
                        <AlertCircle size={14} />
                        {error}
                      </div>
                    ))}
                  </div>
                )}

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
                  <div style={styles.formGroup}>
                    <label style={styles.label}>Task Title *</label>
                    <input 
                      type="text" 
                      name="title" 
                      style={styles.input} 
                      value={formData.title} 
                      onChange={handleInputChange} 
                      required 
                      placeholder="Enter task title"
                    />
                  </div>

                  <div style={styles.formGroup}>
                    <label style={styles.label}>Project *</label>
                    <select 
                      name="projectId" 
                      style={styles.input} 
                      value={formData.projectId} 
                      onChange={handleInputChange} 
                      required
                    >
                      <option value="">Select Project</option>
                      {projects.map(project => (
                        <option key={project.id || project._id} value={project.id || project._id}>
                          {project.name || project.projectName}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div style={styles.formGroup}>
                    <label style={styles.label}>Assign To (Optional)</label>
                    <select 
                      name="assignedTo" 
                      style={styles.input} 
                      value={formData.assignedTo} 
                      onChange={handleInputChange}
                    >
                      <option value="">Unassigned</option>
                      {users.map(user => (
                        <option key={user.id || user._id} value={user.id || user._id}>
                          {user.name || user.fullName} {user.email ? `(${user.email})` : ''}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div style={styles.formGroup}>
                    <label style={styles.label}>Status</label>
                    <select 
                      name="status" 
                      style={styles.input} 
                      value={formData.status} 
                      onChange={handleInputChange}
                    >
                      <option value="To Do">To Do</option>
                      <option value="In Progress">In Progress</option>
                      <option value="Review">Review</option>
                      <option value="Completed">Completed</option>
                      <option value="Blocked">Blocked</option>
                    </select>
                  </div>

                  <div style={styles.formGroup}>
                    <label style={styles.label}>Priority</label>
                    <select 
                      name="priority" 
                      style={styles.input} 
                      value={formData.priority} 
                      onChange={handleInputChange}
                    >
                      <option value="Low">Low</option>
                      <option value="Medium">Medium</option>
                      <option value="High">High</option>
                    </select>
                  </div>

                  <div style={styles.formGroup}>
                    <label style={styles.label}>Start Date</label>
                    <input 
                      type="date" 
                      name="startDate" 
                      style={styles.input} 
                      value={formData.startDate} 
                      onChange={handleInputChange} 
                    />
                  </div>

                  <div style={styles.formGroup}>
                    <label style={styles.label}>Due Date *</label>
                    <input 
                      type="date" 
                      name="dueDate" 
                      style={styles.input} 
                      value={formData.dueDate} 
                      onChange={handleInputChange} 
                      required 
                    />
                  </div>

                  <div style={styles.formGroup}>
                    <label style={styles.label}>Estimated Hours</label>
                    <input 
                      type="number" 
                      name="estimatedHours" 
                      style={styles.input} 
                      value={formData.estimatedHours} 
                      onChange={handleInputChange} 
                      min="0" 
                      step="0.5"
                    />
                  </div>

                  <div style={styles.formGroup}>
                    <label style={styles.label}>Actual Hours</label>
                    <input 
                      type="number" 
                      name="actualHours" 
                      style={styles.input} 
                      value={formData.actualHours} 
                      onChange={handleInputChange} 
                      min="0" 
                      step="0.5"
                    />
                  </div>

                  <div style={styles.formGroup}>
                    <label style={styles.label}>Progress (%)</label>
                    <input 
                      type="number" 
                      name="progress" 
                      style={styles.input} 
                      value={formData.progress} 
                      onChange={handleInputChange} 
                      min="0" 
                      max="100" 
                    />
                  </div>
                </div>

                <div style={styles.formGroup}>
                  <label style={styles.label}>Description</label>
                  <textarea 
                    name="description" 
                    style={styles.textarea} 
                    value={formData.description} 
                    onChange={handleInputChange} 
                  />
                </div>
              </div>

              <div style={styles.modalFooter}>
                <button 
                  type="button" 
                  style={{ ...styles.button, ...styles.secondaryButton }} 
                  onClick={() => { setShowAddModal(false); setShowEditModal(false); }} 
                  disabled={formSubmitting}
                >
                  Cancel
                </button>
                <button type="submit" style={styles.button} disabled={formSubmitting}>
                  {formSubmitting ? 'Saving...' : (showAddModal ? 'Create Task' : 'Update Task')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* View Modal */}
      {showViewModal && selectedTask && (
        <div style={styles.modal} onClick={() => setShowViewModal(false)}>
          <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <h2 style={styles.modalTitle}>Task Details</h2>
              <button 
                style={{ ...styles.actionButton, padding: '8px' }} 
                onClick={() => setShowViewModal(false)}
              >
                <X size={20} />
              </button>
            </div>

            <div style={styles.modalBody}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px' }}>
                <div>
                  <div style={{ fontSize: '12px', color: '#64748b', marginBottom: '4px' }}>Task ID</div>
                  <div style={{ fontSize: '16px', fontWeight: '500', color: '#1e293b' }}>
                    {selectedTask.taskId}
                  </div>
                </div>

                <div>
                  <div style={{ fontSize: '12px', color: '#64748b', marginBottom: '4px' }}>Task Title</div>
                  <div style={{ fontSize: '16px', fontWeight: '500', color: '#1e293b' }}>
                    {selectedTask.title}
                  </div>
                </div>

                <div>
                  <div style={{ fontSize: '12px', color: '#64748b', marginBottom: '4px' }}>Project</div>
                  <div style={{ fontSize: '16px', fontWeight: '500', color: '#1e293b' }}>
                    {getProjectName(selectedTask.projectId)}
                  </div>
                </div>

                <div>
                  <div style={{ fontSize: '12px', color: '#64748b', marginBottom: '4px' }}>Assigned To</div>
                  <div style={{ fontSize: '16px', fontWeight: '500', color: '#1e293b' }}>
                    {getAssigneeName(selectedTask)}
                  </div>
                </div>

                <div>
                  <div style={{ fontSize: '12px', color: '#64748b', marginBottom: '4px' }}>Status</div>
                  <span style={{ 
                    ...styles.badge, 
                    backgroundColor: `${getStatusColor(selectedTask.status)}20`, 
                    color: getStatusColor(selectedTask.status) 
                  }}>
                    {selectedTask.status}
                  </span>
                </div>

                <div>
                  <div style={{ fontSize: '12px', color: '#64748b', marginBottom: '4px' }}>Priority</div>
                  <span style={{ 
                    ...styles.badge, 
                    backgroundColor: `${getPriorityColor(selectedTask.priority)}20`, 
                    color: getPriorityColor(selectedTask.priority) 
                  }}>
                    {selectedTask.priority}
                  </span>
                </div>

                <div>
                  <div style={{ fontSize: '12px', color: '#64748b', marginBottom: '4px' }}>Start Date</div>
                  <div style={{ fontSize: '16px', fontWeight: '500', color: '#1e293b' }}>
                    {selectedTask.startDate ? new Date(selectedTask.startDate).toLocaleDateString() : 'N/A'}
                  </div>
                </div>

                <div>
                  <div style={{ fontSize: '12px', color: '#64748b', marginBottom: '4px' }}>Due Date</div>
                  <div style={{ 
                    fontSize: '16px', 
                    fontWeight: '500', 
                    color: isOverdue(selectedTask) ? '#ef4444' : '#1e293b' 
                  }}>
                    {selectedTask.dueDate ? new Date(selectedTask.dueDate).toLocaleDateString() : 'N/A'}
                  </div>
                  {isOverdue(selectedTask) && (
                    <div style={{ fontSize: '12px', color: '#ef4444', marginTop: '4px' }}>
                      Overdue by {Math.abs(getDaysUntilDue(selectedTask))} days
                    </div>
                  )}
                </div>

                <div>
                  <div style={{ fontSize: '12px', color: '#64748b', marginBottom: '4px' }}>Estimated Hours</div>
                  <div style={{ fontSize: '16px', fontWeight: '500', color: '#1e293b' }}>
                    {selectedTask.estimatedHours}h
                  </div>
                </div>

                <div>
                  <div style={{ fontSize: '12px', color: '#64748b', marginBottom: '4px' }}>Actual Hours</div>
                  <div style={{ fontSize: '16px', fontWeight: '500', color: '#1e293b' }}>
                    {selectedTask.actualHours}h
                  </div>
                </div>

                <div style={{ gridColumn: '1 / -1' }}>
                  <div style={{ fontSize: '12px', color: '#64748b', marginBottom: '8px' }}>Progress</div>
                  <div style={{ fontSize: '16px', fontWeight: '500', color: '#1e293b', marginBottom: '8px' }}>
                    {selectedTask.completionPercentage || 0}%
                  </div>
                  <div style={styles.progressBar}>
                    <div style={{ ...styles.progressFill, width: `${selectedTask.completionPercentage || 0}%` }} />
                  </div>
                </div>
              </div>

              {selectedTask.description && (
                <div style={{ marginTop: '24px' }}>
                  <div style={{ fontSize: '12px', color: '#64748b', marginBottom: '8px' }}>Description</div>
                  <div style={{ 
                    fontSize: '14px', 
                    color: '#1e293b', 
                    padding: '12px', 
                    backgroundColor: '#f8fafc', 
                    borderRadius: '8px' 
                  }}>
                    {selectedTask.description}
                  </div>
                </div>
              )}
            </div>

            <div style={styles.modalFooter}>
              <button 
                style={{ ...styles.button, ...styles.secondaryButton }} 
                onClick={() => { setShowViewModal(false); handleEdit(selectedTask); }}
              >
                <Edit2 size={16} /> Edit Task
              </button>
              <button style={styles.button} onClick={() => setShowViewModal(false)}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TasksPage;